import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { AlertCircle } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const SPEED_INDICES = ["J", "K", "L", "M", "N", "P", "Q", "R", "S", "T", "U", "H", "V", "W", "Y"];

export const SmartSelect = ({ value, onChange, options, placeholder, label, testId, error, required = true }) => {
  return (
    <div>
      <Label className={`input-label ${error ? 'text-[#DC2626]' : ''}`}>{label} {required && '*'}</Label>
      <Select value={value?.toString() || ""} onValueChange={onChange}>
        <SelectTrigger
          className={`input-field ${error ? 'border-[#DC2626] ring-2 ring-[#DC2626]/20' : ''}`}
          data-testid={testId}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt} value={opt.toString()}>{opt}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && <p className="text-xs text-[#DC2626] mt-1">{error}</p>}
    </div>
  );
};

export const BrandAutocomplete = ({ value, onChange, suggestions, error }) => {
  const [open, setOpen] = useState(false);
  const [filtered, setFiltered] = useState([]);

  useEffect(() => {
    if (value && value.length >= 1) {
      const matches = suggestions.filter(s =>
        s.toLowerCase().startsWith(value.toLowerCase()) && s.toLowerCase() !== value.toLowerCase()
      );
      setFiltered(matches.slice(0, 5));
      setOpen(matches.length > 0);
    } else {
      setFiltered([]);
      setOpen(false);
    }
  }, [value, suggestions]);

  return (
    <div className="relative">
      <Label className={`input-label ${error ? 'text-[#DC2626]' : ''}`}>Marque *</Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => filtered.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Michelin"
        className={`input-field ${error ? 'border-[#DC2626] ring-2 ring-[#DC2626]/20' : ''}`}
        data-testid="input-brand"
      />
      {error && <p className="text-xs text-[#DC2626] mt-1">{error}</p>}
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-[#E5E7EB] rounded-sm shadow-lg max-h-40 overflow-auto">
          {filtered.map((s) => (
            <div
              key={s}
              className="px-3 py-2 text-sm cursor-pointer hover:bg-[#F3F4F6]"
              onMouseDown={() => {
                onChange(s);
                setOpen(false);
              }}
            >
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Hook to fetch form values (brands, load_indices) from API
export const useFormValues = () => {
  const [formValues, setFormValues] = useState({ widths: [], heights: [], diameters: [], load_indices: [], brands: [] });

  const fetchFormValues = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tires/form-values`, { withCredentials: true });
      setFormValues(response.data);
    } catch (error) {
      console.error("Error fetching form values:", error);
    }
  }, []);

  useEffect(() => { fetchFormValues(); }, [fetchFormValues]);

  return { formValues, fetchFormValues };
};

// Shared AddTireForm used in both DashboardPage and InventairePage
export const AddTireForm = ({ onClose, onSuccess, formValues, submitLabel, onTireCreated }) => {
  const [formData, setFormData] = useState({
    width: "",
    height: "",
    diameter: "",
    load_index: "",
    speed_index: "",
    brand: "",
    model: "",
    tire_type: "used",
    season: "ete",
    runflat: false,
    camionnette: false,
    condition_score: null,
    dot: "",
    quantity: 1,
    nv: false,
    is_tested: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showGlobalError, setShowGlobalError] = useState(false);

  const widthOptions = [145, 155, 165, 175, 185, 195, 205, 215, 225, 235, 245, 255, 265, 275, 285, 295, 305, 315, 325, 335, 345, 355];
  const heightOptions = [20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85];
  const diameterOptions = [12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24];

  const loadOptions = useMemo(() => {
    const base = [71, 75, 79, 82, 84, 86, 87, 88, 89, 91, 92, 94, 95, 97, 99, 100, 102, 104, 106, 108, 110, 112, 115, 116, 118, 120];
    const merged = new Set([...base, ...(formValues.load_indices || [])]);
    return ["—", ...Array.from(merged).sort((a, b) => a - b)];
  }, [formValues.load_indices]);

  const validateForm = () => {
    const newErrors = {};
    const requiredMessage = "Ce champ est obligatoire";
    if (!formData.width) newErrors.width = requiredMessage;
    if (!formData.height) newErrors.height = requiredMessage;
    if (!formData.diameter) newErrors.diameter = requiredMessage;
    if (!formData.brand || formData.brand.trim() === "") newErrors.brand = requiredMessage;
    if (!formData.model || formData.model.trim() === "") newErrors.model = requiredMessage;
    if (!formData.quantity || formData.quantity < 1) newErrors.quantity = requiredMessage;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { setShowGlobalError(true); return; }
    setShowGlobalError(false);
    setIsLoading(true);
    try {
      const data = {
        ...formData,
        width: parseInt(formData.width),
        height: parseInt(formData.height),
        diameter: parseInt(formData.diameter),
        load_index: formData.load_index && formData.load_index !== "—" ? parseInt(formData.load_index) : null,
        speed_index: formData.speed_index && formData.speed_index !== "—" ? formData.speed_index : null,
        quantity: parseInt(formData.quantity),
        season: formData.season,
        runflat: formData.runflat,
        camionnette: formData.camionnette,
        condition_score: formData.tire_type === "used" && formData.condition_score ? parseInt(formData.condition_score) : null,
        dot: formData.tire_type === "used" ? formData.dot : null,
        nv: formData.nv,
        is_tested: formData.tire_type === "new" ? true : formData.is_tested
      };
      const res = await axios.post(`${API_URL}/api/tires`, data, { withCredentials: true });
      toast.success("Pneu ajouté avec succès");
      if (onTireCreated) {
        onTireCreated(res.data);
      }
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    } finally {
      setIsLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: null });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="add-tire-form">
      {showGlobalError && Object.keys(errors).length > 0 && (
        <div className="p-3 bg-[#FEE2E2] border border-[#FECACA] rounded-sm flex items-start gap-2" data-testid="form-global-error">
          <AlertCircle className="w-4 h-4 text-[#DC2626] mt-0.5 flex-shrink-0" />
          <p className="text-sm text-[#991B1B]">Veuillez remplir tous les champs obligatoires avant de continuer.</p>
        </div>
      )}

      <div>
        <Label className="input-label">Type</Label>
        <div className="flex gap-2">
          <Button type="button" className={formData.tire_type === "used" ? "btn-primary flex-1" : "btn-secondary flex-1"} onClick={() => updateField("tire_type", "used")} data-testid="type-used-btn">Occasion</Button>
          <Button type="button" className={formData.tire_type === "new" ? "btn-primary flex-1" : "btn-secondary flex-1"} onClick={() => updateField("tire_type", "new")} data-testid="type-new-btn">Neuf</Button>
        </div>
      </div>

      <div>
        <Label className="input-label">Saison</Label>
        <div className="flex gap-2">
          <Button type="button" className={formData.season === "ete" ? "btn-primary flex-1" : "btn-secondary flex-1"} onClick={() => updateField("season", "ete")} data-testid="season-ete-btn">Été</Button>
          <Button type="button" className={formData.season === "4saisons" ? "btn-primary flex-1" : "btn-secondary flex-1"} onClick={() => updateField("season", "4saisons")} data-testid="season-4s-btn">4 Saisons</Button>
          <Button type="button" className={formData.season === "hiver" ? "btn-primary flex-1" : "btn-secondary flex-1"} onClick={() => updateField("season", "hiver")} data-testid="season-hiver-btn">Hiver</Button>
        </div>
      </div>

      <div className="flex gap-4">
        <label className="flex items-center gap-2 cursor-pointer" data-testid="runflat-checkbox">
          <Checkbox checked={formData.runflat} onCheckedChange={(v) => updateField("runflat", !!v)} />
          <span className="text-sm text-[#0A0A0A]">RunFlat</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer" data-testid="camionnette-checkbox">
          <Checkbox checked={formData.camionnette} onCheckedChange={(v) => updateField("camionnette", !!v)} />
          <span className="text-sm text-[#0A0A0A]">Camionnette</span>
        </label>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <SmartSelect value={formData.width} onChange={(v) => updateField("width", v)} options={widthOptions} placeholder="205" label="Largeur" testId="input-width" error={errors.width} />
        <SmartSelect value={formData.height} onChange={(v) => updateField("height", v)} options={heightOptions} placeholder="55" label="Hauteur" testId="input-height" error={errors.height} />
        <SmartSelect value={formData.diameter} onChange={(v) => updateField("diameter", v)} options={diameterOptions} placeholder="16" label="Diamètre" testId="input-diameter" error={errors.diameter} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <SmartSelect value={formData.load_index} onChange={(v) => updateField("load_index", v)} options={loadOptions} placeholder="91" label="Indice charge" testId="input-load-index" error={errors.load_index} required={false} />
        <div>
          <Label className="input-label">Indice vitesse</Label>
          <Select value={formData.speed_index} onValueChange={(v) => updateField("speed_index", v)}>
            <SelectTrigger className="input-field" data-testid="input-speed-index"><SelectValue placeholder="V" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="—">—</SelectItem>
              {SPEED_INDICES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <BrandAutocomplete value={formData.brand} onChange={(v) => updateField("brand", v)} suggestions={formValues.brands || []} error={errors.brand} />
        <div>
          <Label className={`input-label ${errors.model ? 'text-[#DC2626]' : ''}`}>Modèle *</Label>
          <Input placeholder="Primacy 4" value={formData.model} onChange={(e) => updateField("model", e.target.value)} className={`input-field ${errors.model ? 'border-[#DC2626] ring-2 ring-[#DC2626]/20' : ''}`} data-testid="input-model" />
          {errors.model && <p className="text-xs text-[#DC2626] mt-1">{errors.model}</p>}
        </div>
      </div>

      {formData.tire_type === "used" && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="input-label">DOT (optionnel)</Label>
              <Input placeholder="2523" maxLength={10} value={formData.dot} onChange={(e) => updateField("dot", e.target.value)} className="input-field" data-testid="input-dot" />
            </div>
            <div>
              <Label className="input-label">Usure (si testé)</Label>
              <Select value={formData.condition_score?.toString() || ""} onValueChange={(v) => updateField("condition_score", v ? parseInt(v) : null)}>
                <SelectTrigger className="input-field" data-testid="input-condition"><SelectValue placeholder="Non testé" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">4 - Excellent</SelectItem>
                  <SelectItem value="3">3 - Bon</SelectItem>
                  <SelectItem value="2">2 - Moyen</SelectItem>
                  <SelectItem value="1">1 - Usé</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="is-tested-checkbox">
              <Checkbox checked={formData.is_tested} onCheckedChange={(v) => updateField("is_tested", !!v)} />
              <span className="text-sm text-[#0A0A0A]">Pneu déjà testé</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="nv-checkbox">
              <Checkbox checked={formData.nv} onCheckedChange={(v) => updateField("nv", !!v)} />
              <span className="text-sm text-[#0A0A0A]">NV</span>
            </label>
          </div>
        </>
      )}

      {formData.tire_type === "new" && (
        <label className="flex items-center gap-2 cursor-pointer" data-testid="nv-checkbox">
          <Checkbox checked={formData.nv} onCheckedChange={(v) => updateField("nv", !!v)} />
          <span className="text-sm text-[#0A0A0A]">NV</span>
        </label>
      )}

      <div className="w-1/2">
        <Label className={`input-label ${errors.quantity ? 'text-[#DC2626]' : ''}`}>Quantité *</Label>
        <Input type="number" min="1" value={formData.quantity} onChange={(e) => updateField("quantity", e.target.value)} className={`input-field ${errors.quantity ? 'border-[#DC2626] ring-2 ring-[#DC2626]/20' : ''}`} data-testid="input-quantity" />
        {errors.quantity && <p className="text-xs text-[#DC2626] mt-1">{errors.quantity}</p>}
      </div>

      <div className="flex gap-2 pt-2" data-testid="add-form-footer">
        <Button type="button" variant="outline" className="btn-secondary flex-1" onClick={onClose}>Annuler</Button>
        <Button type="submit" className="btn-primary flex-1" disabled={isLoading} data-testid="submit-tire-btn">{isLoading ? "Ajout..." : (submitLabel || "Ajouter le pneu")}</Button>
      </div>
    </form>
  );
};
